import {
  all,
  call,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { queryClient } from '..';
import * as actions from '../actions';
import { logCustomEvent, trackEvent } from '../api/analytics';
import { convertTimeToMinutes } from '../components/Request/multiday/utils';
import { EligibleJobAdapter } from '../domain/EligibleJobs';
import {
  getShouldEnableCandidatesListSort,
  getShouldUseInverseBulkConfirmInvites,
  getShouldUseOfficePolicyValidation,
} from '../growthbook';
import history from '../history';
import { JobStatusEnum, WorkerClassificationTypeEnum } from '../models';
import { apiMethods } from '../services/api';
import { apiMethodsConst } from '../services/methods';
import { DashboardJobsType } from '../domain/DashboardJobs';

function* callAndDispatch(method, payload) {
  const response = yield call(apiMethods[method], payload);
  yield put({ type: method, payload: response });
  return response;
}

function* callAndDispatchPayload(method, payload) {
  const response = yield call(apiMethods[method], payload);
  yield put({ type: method, payload });
  return response;
}

const JOB_LIMIT = 15;
function* watchCreateJob() {
  const events = actions.createRequestTypes(actions.CREATE_JOB_ACTION);
  while (true) {
    const data = yield take(actions.CREATE_JOB_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.CREATE_JOB_REQUEST, data);
      const { job } = yield select((store) => store.job);
      const { user } = yield select((store) => store.user);

      const is_info_asked = yield select(
        (store) => store.user.selectedChildOffice?.office_info?.is_info_asked,
      );
      yield logCustomEvent('job_post', data);
      yield put({ type: events.SUCCESS });

      trackEvent('SDB Post Submitted', {
        state: user.state,
        zip: user.zip,
        professional: data.professional,
        rate: data.rate,
        date: data.localDate,
        start: data.localStart,
        end: data.localEnd,
        break: data.break_time,
        'break paid': data.is_break_paid,
        'prefer type': data.prefer_type,
        profession: data.profession,
      });

      const jobData = {
        office: job.offer_owner.office_name,
        date: job.local_date,
        profession: job.professionName,
        subProfession: job.subProfession,
        skills: job.skills,
        local_start_time: job.local_start_time,
        local_end_time: job.local_end_time,
        local_timezone: job.local_timezone,
        rate: job.rate,
        lunch_break_time: job.lunch_break_time,
        is_lunch_break_paid: job.is_lunch_break_paid,
      };

      yield put({
        type: actions.SHOW_CREATE_JOB_MESSAGE,
        payload: {
          route: !is_info_asked ? '/dashboard/nps' : `/dashboard/job/${job.id}`,
          params: { jobId: job.id, isJobPost: true },
          jobData,
        },
      });
    } catch (error) {
      if (error.response?.status === actions.DUPLICATE_JOB_ERROR) {
        yield put({ type: 'CREATE_JOB_ACTION_STOP_LOADING' });
        yield put({ type: actions.IS_OVERLAPPING_JOB, payload: error.response.data.message });
      } else if (error.response?.status === actions.NO_CARD_ERROR) {
        yield put({ type: 'CREATE_JOB_ACTION_STOP_LOADING' });
        yield put({ type: actions.SHOW_JOB_CREATE_PAYMENT_ERROR });
      } else {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
    }
  }
}

function* watchfetchJob() {
  const events = actions.createRequestTypes(actions.FETCH_JOB_ACTION);
  while (true) {
    const {
      jobId,
      candidateId,
      requestCandidateOvertime = true,
    } = yield take(actions.FETCH_JOB_ACTION);

    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatch(apiMethodsConst.FETCH_JOB_REQUEST, jobId);

      const { status, workerClassification } = response || {};

      if (
        requestCandidateOvertime &&
        workerClassification === WorkerClassificationTypeEnum.Employee &&
        (status === JobStatusEnum.Pending || status === JobStatusEnum.Counter) &&
        candidateId
      ) {
        yield* callAndDispatch(apiMethodsConst.FETCH_CANDIDATE_OVERTIME_REQUEST, {
          jobId,
          candidateId,
        });
      }

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFetchJobCandidates() {
  const events = actions.createRequestTypes(actions.FETCH_JOB_CANDIDATES_ACTION);

  while (true) {
    const {
      jobId,
      filters = {},
      resetBestPick = true,
    } = yield take(actions.FETCH_JOB_CANDIDATES_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield put({
        type: actions.JOB_CANDIDATES_FILTER_CHANGE,
        payload: filters,
      });

      const { sort, page } = filters;
      const params = getShouldEnableCandidatesListSort() ? { sortBy: sort } : {};
      const response = yield* callAndDispatch(apiMethodsConst.FETCH_JOB_CANDIDATES_REQUEST, {
        jobId,
        params,
      });

      if (resetBestPick) {
        yield put({
          type: actions.SET_BEST_PICK_CANDIDATES,
          payload: [response?.[0]?.candidate],
        });
      }

      yield put({
        type: actions.SHOW_JOB_CANDIDATES_FOR_PAGE,
        payload: page,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchShowJobCandidatesForPage() {
  function* handler() {
    // get jobId from the URL instead of the store to prevent race conditions issue
    const { pathname } = history.location;
    const match = pathname.match(/dashboard\/job\/([^/]+)\/confirm\/[^/]+/);
    const jobId = match?.[1];

    const { visibleCandidates } = yield select((store) => store.job);

    if (jobId && visibleCandidates.length) {
      history.replace(`/dashboard/job/${jobId}/confirm/${visibleCandidates[0].candidate.id}`);
    }
  }

  yield takeLatest(actions.SHOW_JOB_CANDIDATES_FOR_PAGE, handler);
}

function* watchfetchCandidateJobOvertime() {
  const events = actions.createRequestTypes(actions.FETCH_CANDIDATE_JOB_OVERTIME_ACTION);
  while (true) {
    const { jobId, candidateId } = yield take(actions.FETCH_CANDIDATE_JOB_OVERTIME_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_CANDIDATE_OVERTIME_REQUEST, {
        jobId,
        candidateId,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchGetUserOvertimeLimits() {
  const events = actions.createRequestTypes(actions.GET_USER_OVERTIME_LIMITS_ACTION);
  while (true) {
    yield take(actions.GET_USER_OVERTIME_LIMITS_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.GET_USER_OVERTIME_LIMITS_REQUEST);

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchAverageRating() {
  const events = actions.createRequestTypes(actions.FETCH_AVERAGE_RATING);
  while (true) {
    yield take(actions.FETCH_AVERAGE_RATING);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_AVERAGE_RATING);
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchEditJob() {
  const events = actions.createRequestTypes(actions.EDIT_JOB_ACTION);
  while (true) {
    try {
      const { jobData, jobId } = yield take(actions.EDIT_JOB_ACTION);

      yield put({ type: events.REQUEST });

      const { autoFillFavorites, ...restOfJobData } = jobData;
      const response = yield* callAndDispatch(apiMethodsConst.EDIT_JOB_REQUEST, {
        jobId,
        jobData: {
          ...restOfJobData,
          autofillOptions: {
            favorite: autoFillFavorites,
          },
        },
      });
      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          message: 'The offer has been updated',
          route: 'back',
          isSuccess: true,
        },
      });
      yield put({ type: events.SUCCESS });

      trackEvent('Open Shift Edit Submitted', {
        state: response.data?.state,
        zip: response.data?.zip,
        jobId,
        rate: jobData?.rate,
        date: jobData?.localDate,
        start: jobData?.localStart,
        end: jobData?.localEnd,
        break: jobData?.break_time,
        'break paid': jobData?.is_break_paid,
        'prefer type': jobData?.prefer_type,
        profession: jobData?.profession,
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchGetAllJob() {
  const events = actions.createRequestTypes(actions.GET_ALL_JOBS_ACTION);
  while (true) {
    const { start, end } = yield take(actions.GET_ALL_JOBS_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.GET_ALL_JOBS_REQUEST, { start, end });
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchGetAllJobByStatusDate() {
  const events = actions.createRequestTypes(actions.GET_JOBS_BY_STATUS_DATE_ACTION);
  while (true) {
    const { status } = yield take(actions.GET_JOBS_BY_STATUS_DATE_ACTION);
    const { jobListPagination } = yield select((store) => store.job);

    yield put({ type: events.REQUEST, payload: { status } });
    try {
      if (status?.status !== '') {
        const page = status?.enablePagination ? jobListPagination.page + 1 : 1;

        const response = yield call(apiMethods[apiMethodsConst.GET_JOBS_BY_STATUS_DATE_REQUEST], {
          ...status,
          page,
          date: status.date || jobListPagination?.date,
        });
        yield put({
          type: apiMethodsConst.GET_JOBS_BY_STATUS_DATE_REQUEST,
          payload: { jobs: response, ...status, page },
        });
      }
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchGetLastPostedJobs() {
  const events = actions.createRequestTypes(actions.GET_LAST_POSTED_JOBS);

  function* handler() {
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.GET_LAST_POSTED_JOBS_REQUEST);
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }

  yield takeEvery(actions.GET_LAST_POSTED_JOBS, handler);
}

function* watchFetchJobsWithTransactions() {
  const events = actions.createRequestTypes(actions.FETCH_JOBS_WITH_TRANSACTIONS);
  while (true) {
    const { page, limit, sort, order } = yield take(actions.FETCH_JOBS_WITH_TRANSACTIONS);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_JOBS_WITH_TRANSACTIONS, {
        page,
        limit,
        sort,
        order,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchCancelJob() {
  const events = actions.createRequestTypes(actions.CANCEL_JOB_ACTION);
  while (true) {
    const { jobId, redirect } = yield take(actions.CANCEL_JOB_ACTION);

    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatchPayload(apiMethodsConst.CANCEL_JOB_REQUEST, {
        jobId,
        redirect,
      });
      yield logCustomEvent('cancel_job', { jobId });
      yield put({ type: events.SUCCESS });

      trackEvent('Shift Cancellation Submitted', {
        state: response?.state,
        zip: response?.zip,
        jobId,
        rate: response?.rate,
        date: response?.localDate,
        start: response?.localStart,
        end: response?.localEnd,
        break: response?.break_time,
        'break paid': response?.is_break_paid,
        profession: response?.profession,
      });

      if (redirect) {
        history.push(`/dashboard/feedback/${jobId}`, {
          isOnsiteInterview: response?.isOnsiteInterview,
        });
      } else {
        yield put({
          type: actions.SHOW_MESSAGE,
          payload: {
            title: 'Cancellation Confirmed',
            message: response?.isOnsiteInterview
              ? 'This onsite interview has been successfully cancelled.'
              : 'This shift has been successfully cancelled.',
            isSuccess: true,
          },
        });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFecthCancellationReasons() {
  const events = actions.createRequestTypes(actions.FETCH_CANCELLATION_REASONS);
  while (true) {
    yield take(actions.FETCH_CANCELLATION_REASONS);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_CANCELLATION_REASONS_REQUEST);
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchSetCancellationReasons() {
  const events = actions.createRequestTypes(actions.SUBMIT_CANCELLATION_REASON);
  while (true) {
    const { jobId, cancellationFeedback, cancellationReason } = yield take(
      actions.SUBMIT_CANCELLATION_REASON,
    );

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.SET_CANCELLATION_REASONS_REQUEST, {
        jobId,
        data: { cancellationFeedback, cancellationReason },
      });
      yield put({ type: events.SUCCESS });
      history.push('/dashboard/home');
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

export function* watchConfirmJob() {
  const events = actions.createRequestTypes(actions.CONFIRM_JOB_ACTION);
  while (true) {
    const { jobId, data, counterOfferId, candidate, officeId } = yield take(
      actions.CONFIRM_JOB_ACTION,
    );

    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatch(apiMethodsConst.CONFIRM_JOB_REQUEST, {
        jobId,
        data,
        counterOfferId,
      });
      yield logCustomEvent('confirm_candidate', { ...data, jobId });

      const dashboardJobsInfo = queryClient.getQueryData(['dashboard-jobs-info', DashboardJobsType.ALL]);
      queryClient.clear();
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });

      // Skip the "another job" flow for onsite interviews
      if (response?.isOnsiteInterview) {
        yield put({
          type: actions.SHOW_MESSAGE,
          payload: {
            title: 'Good News!',
            message: 'Your onsite interview has successfully been filled.',
            route: `/permanent-jobs/job-overview/${response?.postingId}`,
            isSuccess: true,
          },
        });
      } else {
        const pendingJobs = yield call(apiMethods.getJobsRequest, {
          limit: 15,
          page: 1,
          professionalId: candidate?.id,
          status: 'pending-counter',
          sortField: 'job_datetime',
          sortOrder: 'asc',
          officeId,
        });

        const nextJob = (pendingJobs || [])?.find(
          (j) => j.id !== jobId && j.multipleCounters.find((c) => c.candidate.id === candidate?.id),
        );

        if (nextJob) {
          yield put({
            type: actions.SHOW_CONFIRM_CANDIDATE_ANOTHER_JOB_POPUP,
            payload: { candidate, officeId },
          });
        } else {
        const shouldUseInverseBulkConfirmInvites = getShouldUseInverseBulkConfirmInvites();

        if (shouldUseInverseBulkConfirmInvites) {
          const eligibleJobsResponse = yield call(apiMethods.getEligibleJobsRequest, {
            professionalId: candidate?.id,
            page: 1,
            limit: 10,
            xOfficeId: officeId,
            profession: response?.profession,
          });

          const hasEligibleJobs = eligibleJobsResponse?.response?.length > 0;

          if (hasEligibleJobs) {
            // Transform and cache the data for React Query to avoid refetching
            const transformedResult =
              EligibleJobAdapter.toGetEligibleJobsResult(eligibleJobsResponse);
            const queryKey = [
              'eligible-jobs-infinite',
              {
                professionalId: candidate?.id,
                limit: 10,
                enabled: true,
                profession: response?.profession,
              },
            ];

            queryClient.setQueryData(queryKey, {
              pages: [transformedResult],
              pageParams: [1],
            });

            yield put({
              type: actions.SHOW_CONFIRM_CANDIDATE_ELIGIBLE_JOBS_POPUP,
              payload: {
                candidate,
                professionalId: candidate?.id,
                officeId,
                job: response,
              },
            });
            return;
          }
        }
          yield put({
            type: actions.SHOW_MESSAGE,
            payload: {
              title: 'Good News!',
              message: 'Your shift has successfully been filled.',
              route: `/dashboard/jobs/${dashboardJobsInfo?.pendingCount > 1 ? 'pending' : 'scheduled'}`,
              isSuccess: true,
            },
          });
        }
      }

      yield put({ type: events.SUCCESS });

      trackEvent('Confirm Candidate Attempted', {
        state: response.data?.state,
        zip: response.data?.zip,
        jobId,
        rate: response.data?.rate,
        date: response.data?.localDate,
        start: response.data?.localStart,
        end: response.data?.localEnd,
        break: response.data?.break_time,
        'break paid': response.data?.is_break_paid,
        profession: response.data?.profession,
      });
    } catch (error) {
      if (error.response?.status === actions.NO_CARD_ERROR) {
        yield put({ type: 'CONFIRM_JOB_ACTION_STOP_LOADING' });
        yield put({ type: actions.SHOW_PAYMENT_MISSING_POPUP });
      } else {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
    }
  }
}

function* watchUpdateJob() {
  const events = actions.createRequestTypes(actions.UPDATE_JOB_ACTION);
  while (true) {
    const { jobData } = yield take(actions.UPDATE_JOB_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield put({ type: events.SUCCESS, payload: jobData });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchReportIncident() {
  const events = actions.createRequestTypes(actions.REPORT_INCIDENT_ACTION);

  function* handler({ jobId, incidentType, repostMaxTime }) {
    try {
      yield put({ type: events.REQUEST });

      yield* callAndDispatch(apiMethodsConst.REPORT_INCIDENT_REQUEST, {
        jobId,
        type: incidentType,
        repostMaxStartTime: repostMaxTime,
      });
      yield put({ type: events.SUCCESS, payload: jobId });
      if (repostMaxTime) {
        yield put({ type: actions.SHOW_SHIFT_REPOSTED_POPUP });
      } else {
        yield put({ type: actions.SHOW_INCIDENT_REPORTED_POPUP });
      }
    } catch (error) {
      yield put({
        type: events.FAILURE,
        payload: { ...error, message: error?.response?.data?.message || error?.message },
      });
      yield put({ type: actions.CLEAR_INCIDENT_POPUPS });
      yield put({ type: actions.RESET_INCIDENT });
    }
  }

  yield takeEvery(actions.REPORT_INCIDENT_ACTION, handler);
}

function* watchCloseOverlappingModal() {
  const events = actions.createRequestTypes(actions.CLOSE_OVERLAPPING_MODAL);

  try {
    yield take(actions.CLOSE_OVERLAPPING_MODAL);
    yield put({ type: events.REQUEST });

    yield put({ type: events.SUCCESS });
  } catch (error) {
    yield put({ type: events.FAILURE, payload: error });
  }
}

export function* postInvites(professionalIds, jobIds) {
  yield callAndDispatch(apiMethodsConst.INVITE_PROFESSIONALS_TO_JOBS_REQUEST, {
    professionalIds,
    jobIds,
  });
}

export function* watchInviteToMultiDayPosting() {
  const events = actions.createRequestTypes(actions.MULTIDAY_BOOKING_INVITE_POST);
  while (true) {
    const { officeId } = yield take(actions.MULTIDAY_BOOKING_INVITE_POST);

    yield put({ type: events.REQUEST });
    try {
      const jobInfo = yield select((state) =>
        state.multiday.jobInfo.map(({ autoFillFavorites, ...restOfJob }) => ({
          ...restOfJob,
          break_time: convertTimeToMinutes(restOfJob.break_time),
          rate: parseFloat(restOfJob.rate),
          autoFill: state.job.showJobCreatePaymentError ? false : restOfJob.autoFill,
          autofillOptions: {
            favorite: state.job.showJobCreatePaymentError ? false : autoFillFavorites,
          },
          officeId: undefined,
        })),
      );

      // Post jobs synchronously
      const response = yield* callAndDispatch(apiMethodsConst.MULTIDAY_JOB_POSTING_REQUEST, {
        jobInfo,
        officeId,
      });
      const professionalIds = jobInfo?.[0]?.professionals;
      // Post invites asynchronously
      if (professionalIds?.length) {
        yield fork(postInvites, professionalIds, response?.jobs);
      }
      history.replace('/');

      if (professionalIds?.length) {
        yield logCustomEvent('rebook_post', jobInfo);
        trackEvent('Rebooking Job', {
          professionalIds,
          numberOfJobs: response?.jobs?.length,
        });
        yield put({
          type: actions.SHOW_MESSAGE,
          payload: {
            title: 'Shifts Posted & Invites Successfully Sent!',
            message: (
              <span>
                Your shifts were sent to the GoTu community, and invited professionals received
                alerts for days they’re available.
                <br />
                Note: Professionals won’t receive invites for days in which they’re unavailable.
              </span>
            ),
            isSuccess: true,
            buttonText: 'Close',
          },
        });
      } else {
        yield logCustomEvent('multiday_post', jobInfo);
        const jobType = jobInfo?.length > 1 ? 'MDB' : 'SDB';
        trackEvent(`${jobType} Post Submitted`, { jobInfo });
        if (jobInfo?.[0]?.autofillOptions?.favorite) {
          yield put({
            type: actions.SHOW_MESSAGE,
            payload: {
              title: 'Shift Posted Successfully!',
              message:
                'We’re reaching out to all professionals in your area. Your shifts were sent successfully to the GoTu community. You will be notified when your offers have been accepted.',
              isSuccess: true,
            },
          });
        } else {
          yield put({
            type: actions.SHOW_JOBS_INVITE_CONFIRMATION_POPUP,
            payload: {
              jobIds: response?.jobs,
              profession: response?.profession,
              officeId: officeId,
            },
          });
        }
      }
      yield delay(500);
      yield put({ type: events.SUCCESS });
    } catch (error) {
      const axiosError = error;
      const response = axiosError?.response;
      const status = response?.status;
      const errorMessage = response?.data?.message || '';

      if (status === actions.DUPLICATE_JOB_ERROR) {
        yield put({ type: actions.IS_OVERLAPPING_JOB, payload: error.response.data.message });
        yield put({ type: 'MULTIDAY_BOOKING_INVITE_POST_STOP_LOADING' });
      } else if (status === actions.NO_CARD_ERROR) {
        yield put({ type: actions.SHOW_JOB_CREATE_PAYMENT_ERROR });
        yield put({ type: 'MULTIDAY_BOOKING_INVITE_POST_STOP_LOADING' });
      } else if (
        errorMessage.includes('Cannot post shifts') &&
        getShouldUseOfficePolicyValidation()
      ) {
        yield put({ type: 'MULTIDAY_BOOKING_INVITE_POST_STOP_LOADING' });

        const popupActions = [
          {
            substring: "GoTu's Policies",
            action: actions.SHOW_CHILD_MISSING_TERMS_POPUP,
            payload: {
              isTCMissing: true,
              isPSAMissing: true,
            },
          },
          {
            substring: "GoTu's Terms and Conditions",
            action: actions.SHOW_CHILD_MISSING_TERMS_POPUP,
            payload: {
              isTCMissing: true,
              isPSAMissing: false,
            },
          },
          {
            substring: "GoTu's Personnel Service Agreement",
            action: actions.SHOW_CHILD_MISSING_TERMS_POPUP,
            payload: {
              isTCMissing: false,
              isPSAMissing: true,
            },
          },
        ];

        const matchedPopup = popupActions.find(({ substring }) => errorMessage.includes(substring));

        if (matchedPopup) {
          yield put({
            type: matchedPopup.action,
            payload: { ...matchedPopup.payload, officeId },
          });
        } else {
          yield put({ type: events.FAILURE, payload: error.response?.data });
        }
      } else {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
    }
  }
}

function* watchCloseChildMissingTermsPopup() {
  const events = actions.createRequestTypes(actions.HIDE_CHILD_MISSING_TERMS_POPUP);
  while (true) {
    yield take(actions.HIDE_CHILD_MISSING_TERMS_POPUP);
    yield put({ type: events.SUCCESS });
  }
}

function* watchPPPosting() {
  const events = actions.createRequestTypes(actions.POST_PP_JOB);
  while (true) {
    const { jobData } = yield take(actions.POST_PP_JOB);

    const currentUser = yield select(
      (store) => store.user?.selectedChildOffice || store.user?.user,
    );

    // eslint-disable-next-line max-len
    const fmtJobData = `Start Date: ${jobData?.start_date}; Position: ${jobData?.position}; Rate: ${jobData?.rate}; Benefits: ${jobData?.benefits}; Profession: ${jobData?.profession}; SubProfession: ${jobData?.subProfession}; Content: ${jobData?.content};`;

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.PP_POSTING_REQUEST, {
        text: fmtJobData,
        workingInterview: jobData?.workingInterview,
      });
      yield put({ type: events.SUCCESS });
      yield logCustomEvent('permanent_post', jobData);

      trackEvent('Office PP form Submitted');

      trackEvent('Permanent Hire Request Submitted', {
        additionalContacts: currentUser?.account_admin,
        city: currentUser?.address?.[0]?.city,
        currentScreen: 'Permanent Hire',
        email: currentUser?.email_id,
        firstName: currentUser?.first_name,
        PermHireOpenTextField: fmtJobData,
      });

      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          title: 'Request Successfully Submitted!',
          message: 'Your Permanent Hire request has been submitted.',
          route: '/',
          isSuccess: true,
          buttonText: 'Close',
        },
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchSelectDatesMDB() {
  actions.createRequestTypes(actions.SELECT_DAYS_MDB);
  while (true) {
    yield take(actions.SELECT_DAYS_MDB);
  }
}

function* watchJobInfoMDB() {
  actions.createRequestTypes(actions.SELECT_DEFAULT_INFO_MDB);

  while (true) {
    yield take(actions.SELECT_DEFAULT_INFO_MDB);
  }
}

function* watchOpenJobs() {
  const events = actions.createRequestTypes(actions.FETCH_OPEN_JOBS_ACTION);

  while (true) {
    const { page } = yield take(actions.FETCH_OPEN_JOBS_ACTION);

    try {
      const { dashboardJobListPagination } = yield select((store) => store.job);
      const state = {
        page,
        limit: JOB_LIMIT,
      };
      const oldState = {
        page: dashboardJobListPagination.page,
        limit: dashboardJobListPagination.limit,
      };
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(oldState) !== JSON.stringify(state)) ||
        !dashboardJobListPagination.isActiveRequest ||
        !dashboardJobListPagination.isFinal
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_OPEN_JOB_REQUEST, state);

        yield put({ type: events.SUCCESS });

        const openJobs = yield select((store) => store.job.openJobs);
        trackEvent('Shifts - Open Shifts Tab Clicked', { openShiftsCount: openJobs?.length });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data || error });
    }
  }
}

function* watchScheduledJobs() {
  const events = actions.createRequestTypes(actions.FETCH_SCHEDULED_JOBS_ACTION);
  while (true) {
    const { page } = yield take(actions.FETCH_SCHEDULED_JOBS_ACTION);

    try {
      const { dashboardJobListPagination } = yield select((store) => store.job);
      const state = {
        page,
        limit: JOB_LIMIT,
      };
      const oldState = {
        page: dashboardJobListPagination.page,
        limit: dashboardJobListPagination.limit,
      };
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(oldState) !== JSON.stringify(state)) ||
        !dashboardJobListPagination.isActiveRequest ||
        !dashboardJobListPagination.isFinal
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_SCHEDULED_JOBS_REQUEST, state);
        yield put({ type: events.SUCCESS });

        const scheduledJobs = yield select((store) => store.job.scheduledJobs);
        trackEvent('Shifts - Scheduled Shifts Tab Clicked', {
          scheduledShiftsCount: scheduledJobs?.length,
        });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data || error });
    }
  }
}

function* watchActionRequiredJobs() {
  const events = actions.createRequestTypes(actions.FETCH_ACTION_REQUIRED_JOBS_ACTION);
  while (true) {
    const { page } = yield take(actions.FETCH_ACTION_REQUIRED_JOBS_ACTION);

    try {
      const { dashboardJobListPagination } = yield select((store) => store.job);
      const state = {
        page,
        limit: JOB_LIMIT,
      };
      const oldState = {
        page: dashboardJobListPagination.page,
        limit: dashboardJobListPagination.limit,
      };
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(oldState) !== JSON.stringify(state)) ||
        !dashboardJobListPagination.isActiveRequest ||
        !dashboardJobListPagination.isFinal
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_ACTION_REQUIRED_JOBS_REQUEST, state);
        yield put({ type: events.SUCCESS });

        const pendingJobs = yield select((store) => store.job.actionRequiredJobs);
        trackEvent('Shifts - Action Needed Tab Clicked', {
          actionNeededShiftsCount: pendingJobs?.length,
        });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchActionTodayJobs() {
  const events = actions.createRequestTypes(actions.FETCH_TODAY_JOBS_ACTION);
  while (true) {
    const { page } = yield take(actions.FETCH_TODAY_JOBS_ACTION);

    try {
      const { dashboardJobListPagination } = yield select((store) => store.job);
      const state = {
        page,
        limit: JOB_LIMIT,
      };
      const oldState = {
        page: dashboardJobListPagination.page,
        limit: dashboardJobListPagination.limit,
      };
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(oldState) !== JSON.stringify(state)) ||
        !dashboardJobListPagination.isActiveRequest ||
        !dashboardJobListPagination.isFinal
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_TODAY_JOBS_REQUEST, state);

        yield put({ type: events.SUCCESS });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchDeclineCounterOffer() {
  const events = actions.createRequestTypes(actions.DECLINE_COUNTER_OFFER);
  while (true) {
    const { jobId, counterOfferId } = yield take(actions.DECLINE_COUNTER_OFFER);

    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatch(apiMethodsConst.DECLINE_COUNTER_OFFER_REQUEST, {
        jobId,
        counterOfferId,
      });
      yield logCustomEvent('decline_counter_offer', {
        jobId,
        counterOfferId,
      });

      trackEvent('Decline Counter offer', {
        state: response.data?.state,
        zip: response.data?.zip,
        jobId,
        rate: response.data?.rate,
        date: response.data?.localDate,
        start: response.data?.localStart,
        end: response.data?.localEnd,
      });

      history.push(`/dashboard/job/${jobId}`);

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchDeclineCandidate() {
  const events = actions.createRequestTypes(actions.DECLINE_CANDIDATE);
  while (true) {
    const { jobId, counterOfferId, cancellation_reason, candidateId } = yield take(
      actions.DECLINE_CANDIDATE,
    );
    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatch(apiMethodsConst.DECLINE_COUNTER_OFFER_REQUEST, {
        jobId,
        counterOfferId,
        cancellation_reason,
      });
      yield logCustomEvent('decline_candidate', {
        jobId,
        counterOfferId,
        candidateId,
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });
      yield put({ type: events.SUCCESS, payload: { id: candidateId } });

      const isOnsiteInterview = response?.isOnsiteInterview;

      trackEvent('Decline Candidate Button Clicked', {
        state: response?.state,
        zip: response?.zip,
        jobId,
        rate: response?.rate,
        date: response?.localDate,
        start: response?.localStart,
        end: response?.localEnd,
      });

      if (isOnsiteInterview) {
        continue;
      }

      const allCandidates = yield select((store) => store.job.allCandidates);
      const declinedIndex = allCandidates.findIndex(
        (counterOffer) => counterOffer.id === counterOfferId,
      );

      let nextCounterOffer = null;
      if (declinedIndex !== -1) {
        // If the declined candidate is not the last one, select the next one
        nextCounterOffer = allCandidates[declinedIndex + 1];

        if (!nextCounterOffer) {
          // If the declined candidate is the last one, select the previous one
          nextCounterOffer = allCandidates[declinedIndex - 1];
        }
      }

      if (nextCounterOffer) {
        history.replace(`/dashboard/job/${jobId}/confirm/${nextCounterOffer.candidate.id}`, {
          fromDeclineCandidate: true,
        });
      } else {
        history.replace(`/dashboard/job/${jobId}`);
      }
    } catch (error) {
      if (error.response?.status === actions.NO_CARD_ERROR) {
        yield put({ type: 'DECLINE_CANDIDATE_STOP_LOADING' });
        yield put({ type: actions.SHOW_PAYMENT_MISSING_POPUP });
      } else {
        yield put({ type: events.FAILURE, payload: error.response?.data });
      }
    }
  }
}

function* watchFetchCounterOffers() {
  const events = actions.createRequestTypes(actions.FETCH_EXPIRED_COUNTER_OFFERS);
  while (true) {
    const { jobId } = yield take(actions.FETCH_EXPIRED_COUNTER_OFFERS);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_EXPIRED_COUNTER_OFFERS, jobId);
      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFetchReceipts() {
  const events = actions.createRequestTypes(actions.FETCH_RECEIPT_LIST);
  while (true) {
    const { state } = yield take(actions.FETCH_RECEIPT_LIST);
    try {
      const { receiptsPagination } = yield select((store) => store.job);
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(receiptsPagination) !== JSON.stringify(state))
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_RECEIPTS_REQUEST, state);

        yield put({
          type: events.SUCCESS,
        });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}
function* watchFetchReceiptsReport() {
  const events = actions.createRequestTypes(actions.FETCH_RECEIPTS_REPORT);
  while (true) {
    try {
      const { state } = yield take(actions.FETCH_RECEIPTS_REPORT);

      yield put({ type: events.REQUEST, payload: state });

      yield* callAndDispatch(apiMethodsConst.FETCH_RECEIPTS_REPORT_REQUEST, state);

      yield put({
        type: events.SUCCESS,
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchChangeSchedule() {
  actions.createRequestTypes(actions.CHANGE_SCHEDULE);
  while (true) {
    yield take(actions.CHANGE_SCHEDULE);
  }
}

function* watchAdjustmentRequest() {
  const events = actions.createRequestTypes(actions.ADJUST_HOURS_ACTION);
  while (true) {
    const { jobId, data, navigateToRating } = yield take(actions.ADJUST_HOURS_ACTION);
    yield put({ type: events.REQUEST });
    try {
      const response = yield* callAndDispatch(apiMethodsConst.ADJUST_HOURS_REQUEST, {
        jobId,
        data,
      });
      yield logCustomEvent('adjustment_request', {
        jobId,
        data,
      });
      yield put({ type: events.SUCCESS, payload: jobId });

      trackEvent('Adjust Job', {
        state: response.data?.state,
        zip: response.data?.zip,
        jobId,
        'new date': data.localDate,
        'new break': data.break,
        'new start time': data.localStart,
        'new end time': data.localEnd,
      });

      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          title: 'Adjustment Request Submitted',
          message:
            'Your adjustment has been received and sent to the professional for review. Once they confirm this reported time adjustment, all charges related to this shift will be adjusted accordingly.',
          route: navigateToRating ? `/dashboard/job/${jobId}/feedback-rating` : 'back',
          isSuccess: true,
        },
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFetchInvoices() {
  const events = actions.createRequestTypes(actions.FETCH_INVOICE_LIST);
  while (true) {
    const { state } = yield take(actions.FETCH_INVOICE_LIST);
    try {
      const { invoicesPagination } = yield select((store) => store.job);
      if (
        state.page === 1 ||
        (JSON.stringify(state) && JSON.stringify(invoicesPagination) !== JSON.stringify(state))
      ) {
        yield put({ type: events.REQUEST, payload: state });

        yield* callAndDispatch(apiMethodsConst.FETCH_INVOICES_REQUEST, state);

        yield put({
          type: events.SUCCESS,
        });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchCreateLocumTenens() {
  const events = actions.createRequestTypes(actions.CREATE_LOCUM_TENENS_DENTIST_ACTION);
  while (true) {
    const { text } = yield take(actions.CREATE_LOCUM_TENENS_DENTIST_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.CREATE_LOCUM_TENENS_DENTIST_REQUEST, text);
      yield put({ type: events.SUCCESS });
      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          title: 'Request Successfully Sent!',
          message:
            'We’ve received your request. Someone from GoTu will reach out shortly with next steps.',
          route: '/dashboard/locum-tenens-dentist',
          isSuccess: true,
        },
      });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFetchOpenJobsForRecentProfessional() {
  const events = actions.createRequestTypes(actions.FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION);
  while (true) {
    const {
      payload: { page, professionalId, profession, officeId },
    } = yield take(actions.FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION);

    try {
      const { recentOpenJobs } = yield select((store) => store.job);
      if (
        page === 1 ||
        page !== recentOpenJobs.page ||
        !recentOpenJobs.isActiveRequest ||
        !recentOpenJobs.isFinal
      ) {
        yield put({ type: events.REQUEST });
        const method = apiMethodsConst.FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_REQUEST;
        const response = yield call(apiMethods[method], {
          professionalId,
          profession,
          limit: recentOpenJobs.limit,
          page,
          officeId,
        });
        yield put({ type: method, payload: { ...response, professionalId } });
        yield put({ type: events.SUCCESS });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data || error });
    }
  }
}

function* watchInviteProfessionalsToJobs() {
  const events = actions.createRequestTypes(actions.INVITE_PROFESSIONALS_TO_JOBS);
  while (true) {
    const { professionalIds, jobIds, isJobsInvite, officeId } = yield take(
      actions.INVITE_PROFESSIONALS_TO_JOBS,
    );

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.INVITE_PROFESSIONALS_TO_JOBS_REQUEST, {
        professionalIds,
        jobIds,
        officeId,
      });

      if (isJobsInvite) {
        yield put({ type: actions.HIDE_JOBS_INVITE_POPUP });
        yield delay(500);
        yield put({
          type: actions.SHOW_MESSAGE,
          payload: {
            title: 'Invite(s) Successfully Sent!',
            message: (
              <span>
                Your shifts were sent to the GoTu community, and invited professionals received
                alerts for days they’re available.
                <br />
                Note: Professionals won’t receive invites for days in which they’re unavailable.
              </span>
            ),
            isSuccess: true,
            action: actions.INVITE_PROFESSIONALS_TO_JOBS,
            buttonText: 'Close',
          },
        });
      }

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

function* watchFetchOfficeJobInvites() {
  const events = actions.createRequestTypes(actions.FETCH_OFFICE_JOB_INVITES_ACTION);

  while (true) {
    const {
      payload: { page },
    } = yield take(actions.FETCH_OFFICE_JOB_INVITES_ACTION);

    try {
      const { officeJobInvites } = yield select((store) => store.job);
      const params = {
        page,
        limit: 10,
      };
      const oldParams = {
        page: officeJobInvites.page,
        limit: officeJobInvites.limit,
      };
      if (
        params.page === 1 ||
        (JSON.stringify(params) && JSON.stringify(params) !== JSON.stringify(oldParams)) ||
        !officeJobInvites.isActiveRequest ||
        !officeJobInvites.isFinal
      ) {
        yield put({ type: events.REQUEST, payload: params });
        yield* callAndDispatch(apiMethodsConst.FETCH_OFFICE_JOB_INVITES_REQUEST, params);
        yield put({ type: events.SUCCESS });
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data || error });
    }
  }
}

export default function* sagaJob() {
  yield all([
    fork(watchCreateJob),
    fork(watchfetchJob),
    fork(watchFetchJobCandidates),
    fork(watchShowJobCandidatesForPage),
    fork(watchAverageRating),
    fork(watchCancelJob),
    fork(watchEditJob),
    fork(watchConfirmJob),
    fork(watchUpdateJob),
    fork(watchGetAllJob),
    fork(watchGetAllJobByStatusDate),
    fork(watchFetchJobsWithTransactions),
    fork(watchReportIncident),
    fork(watchCloseOverlappingModal),
    fork(watchPPPosting),
    fork(watchSelectDatesMDB),
    fork(watchJobInfoMDB),
    fork(watchOpenJobs),
    fork(watchScheduledJobs),
    fork(watchActionRequiredJobs),
    fork(watchActionTodayJobs),
    fork(watchChangeSchedule),
    fork(watchDeclineCounterOffer),
    fork(watchDeclineCandidate),
    fork(watchFetchCounterOffers),
    fork(watchFetchReceipts),
    fork(watchAdjustmentRequest),
    fork(watchFetchReceiptsReport),
    fork(watchFetchInvoices),
    fork(watchFecthCancellationReasons),
    fork(watchSetCancellationReasons),
    fork(watchCreateLocumTenens),
    fork(watchGetUserOvertimeLimits),
    fork(watchFetchOpenJobsForRecentProfessional),
    fork(watchInviteProfessionalsToJobs),
    fork(watchFetchOfficeJobInvites),
    fork(watchInviteToMultiDayPosting),
    fork(watchfetchCandidateJobOvertime),
    fork(watchGetLastPostedJobs),
    fork(watchCloseChildMissingTermsPopup),
  ]);
}
